/* eslint-disable no-param-reassign */

export const ACCORDION_ITEM_CLASS = 'ComparisonGrid__accordionItem';
export const EMPTY_ACCORDION_ITEM_CLASS = 'ComparisonGrid__emptyAccordionItem';
export const TOUR_TITLE_CLASS = 'ComparisonGrid__tourTitle';

/**
 * Adjusts the heights of accordion elements and their child items to match the tallest elements,
 * ensuring alignment based on their open/closed state.
 *
 * @param {string} label - The label of the row being toggled.
 * @param {boolean} accordionIsNowOpen - Indicates if the accordion is now open.
 * @param {Object} accordionRefs - A reference object containing DOM elements for each accordion row.
 * @returns {void}
 */
export const adjustAccordionHeights = (label, accordionIsNowOpen, accordionRefs) => {
  const elementsInAccordionRow = Object.values(accordionRefs.current[label]);

  if (!accordionIsNowOpen) {
    // Reset minHeight for the accordion containers and their child items
    elementsInAccordionRow.forEach((el) => {
      if (el) {
        el.style.minHeight = '';
        // Reset minHeight for child elements
        const childItems = el.querySelectorAll(
          `.${ACCORDION_ITEM_CLASS}, .${EMPTY_ACCORDION_ITEM_CLASS}`
        );
        childItems.forEach((child) => {
          child.style.minHeight = '';
        });
      }
    });
    return;
  }

  // Reset minHeight for all accordion containers
  elementsInAccordionRow.forEach((el) => {
    if (el) {
      el.style.minHeight = '';
    }
  });

  // Calculate the maximum height among the accordion containers
  const maxContainerHeight = Math.max(
    ...elementsInAccordionRow.map((el) => (el ? el.offsetHeight : 0))
  );

  // Set all accordion containers to the maximum height
  elementsInAccordionRow.forEach((el) => {
    if (el) {
      el.style.minHeight = `${maxContainerHeight}px`;
    }
  });

  // Collect all child items from each accordion
  const itemsPerAccordion = elementsInAccordionRow.map((el) =>
    el
      ? Array.from(el.querySelectorAll(`.${ACCORDION_ITEM_CLASS}, .${EMPTY_ACCORDION_ITEM_CLASS}`))
      : []
  );

  // Find the maximum number of items in any accordion
  const maxItemsCount = Math.max(...itemsPerAccordion.map((items) => items.length));

  // Loop through each item index
  Array.from({ length: maxItemsCount }).forEach((_, index) => {
    const itemsAtIndex = itemsPerAccordion.map((items) => items[index]).filter((item) => item);

    // Reset minHeight for items at this index
    itemsAtIndex.forEach((item) => {
      item.style.minHeight = '';
    });

    // Calculate the maximum height among items at the current index
    const maxItemHeight = Math.max(...itemsAtIndex.map((item) => item.offsetHeight));

    // Set minHeight of items at the current index to maxItemHeight
    itemsAtIndex.forEach((item) => {
      item.style.minHeight = `${maxItemHeight}px`;
    });
  });
};

export const calculateMaxTourTitleHeight = () => {
  const tourTitleElements = document.querySelectorAll(`.${TOUR_TITLE_CLASS}`);
  const maxHeight = Array.from(tourTitleElements).reduce((max, el) => {
    // Store the original height
    const originalHeight = el.style.height;

    // Temporarily set height to auto to measure full content
    el.style.height = 'auto';
    const height = el.scrollHeight; // Measure the full content height

    // Reset to original height
    el.style.height = originalHeight;

    return height > max ? height : max;
  }, 70);

  // Set the CSS variable
  document.documentElement.style.setProperty('--tour-name-height', `${maxHeight}px`);
};
